import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormModal } from '../../../modal/form-modal';
import { AppButton } from '../../../button/app-button';
import { Stepper } from '../../../stepper/stepper';
import { Field, FieldError, FieldHint, FieldRow, Input, Label, Select } from '../../../form-kit';
import { Shake, StatusLabel } from '../../../../motion';
import toast from '../../../../utils/toast';
import { deviceZone, zoneLabel } from '../../../../function-utils/building-time';
import { listStaysHosts, promoteUnit, StaysApiError, updateStaysListing } from '../../../../api/stays-client';
import type { StaysHost, StaysListing, StaysPatchInput } from '../../../../interfaces/stays';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import { COUNTRY_CURRENCY, STAYS_COUNTRIES, STAYS_CURRENCIES, countryName, refusalText } from './stays-labels';
import { FormAlert, ReadValue } from './dashboard-stays.styled';

/**
 * PROMOTE TO STAYS / EDIT LISTING BASICS — one form, two modes (ai-plans/stays
 * plan S1, screen S-5).
 *
 * Promote: a unit becomes a listing. "Save as draft" keeps it off Stays until
 * someone makes it live; "Promote to Stays" makes it live at once.
 * Edit: the basics of an existing listing. Only the fields that CHANGED are
 * sent, with the listing's version — a listing someone else changed meanwhile
 * answers 409 and the page reads it again (no silent overwrite).
 *
 * The check-in / check-out times are the BUILDING's wall clock (a policy, not
 * an instant), labelled with the building's zone.
 *
 * The country is the BUILDING's (user, 2026-10-27: "remove country"): shown,
 * never chosen. The currency starts as that country's. A building with no
 * country Stays covers cannot be promoted from here; the form says to add the
 * building's location first.
 */

export interface PromoteUnitInfo {
  unit_id: number;
  unit_number: string;
  unit_name: string | null;
  building_id: number;
  building_name: string;
  building_timezone: string;
  building_country: string | null;
}

export interface ListingBasicsModalProps {
  open: boolean;
  mode: 'promote' | 'edit';
  /** promote mode */
  unit?: PromoteUnitInfo | null;
  /** edit mode */
  listing?: StaysListing | null;
  onClose: () => void;
  /** The saved listing (or, on a 409 "changed", the server's current one). */
  onSaved: (listing: StaysListing) => void;
}

type Form = {
  display_name: string;
  currency: string;
  check_in_time: string;
  check_out_time: string;
  max_guests: number;
  host_user_id: string;
};

const FIELD_ORDER = ['display_name', 'currency', 'check_in_time', 'check_out_time', 'max_guests', 'host_user_id'];
const knownCountry = (c: string | null | undefined) => (c && STAYS_COUNTRIES.some(x => x.code === c) ? c : '');

const initialForm = (mode: 'promote' | 'edit', unit?: PromoteUnitInfo | null, listing?: StaysListing | null): Form => {
  if (mode === 'edit' && listing) {
    return {
      display_name: listing.display_name,
      currency: listing.currency,
      check_in_time: listing.check_in_time,
      check_out_time: listing.check_out_time,
      max_guests: listing.max_guests,
      host_user_id: listing.host?.id || '',
    };
  }
  const country = knownCountry(unit?.building_country);

  return {
    display_name: unit ? unit.unit_name || `Unit ${unit.unit_number}` : '',
    currency: country ? COUNTRY_CURRENCY[country] || '' : '',
    check_in_time: '15:00',
    check_out_time: '11:00',
    max_guests: 2,
    host_user_id: '',
  };
};

export const ListingBasicsModal: React.FC<ListingBasicsModalProps> = ({ open, mode, unit, listing, onClose, onSaved }) => {
  const uid = useId();
  const formId = `${uid}-listing-basics`;
  const fid = (f: string) => `${uid}-${f}`;
  // Independent Stays IS2: property words without Desk.
  const desk = useDeskInPlan();
  const eid = (f: string) => `${uid}-${f}-error`;

  const [form, setForm] = useState<Form>(() => initialForm(mode, unit, listing));
  const [initial, setInitial] = useState<Form>(form);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState<null | 'live' | 'draft' | 'edit'>(null);
  const [hosts, setHosts] = useState<StaysHost[]>([]);
  const busy = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);

  // A fresh form every time the modal opens on something.
  useEffect(() => {
    if (!open) return;
    const f = initialForm(mode, unit, listing);
    setForm(f);
    setInitial(f);
    setErrors({});
    setRefusal(null);
    setAttempt(0);
  }, [open, mode, unit, listing]);

  useEffect(() => {
    if (!open || mode !== 'edit' || !listing) return;
    let alive = true;
    listStaysHosts(listing.building_id)
      .then(r => {
        if (alive) setHosts(r.items);
      })
      .catch(() => {
        if (alive) setHosts(listing.host ? [{ id: listing.host.id, name: listing.host.name, role: 'owner' }] : []);
      });

    return () => {
      alive = false;
    };
  }, [open, mode, listing]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  // The building's country ('' when it has none Stays covers).
  const country = mode === 'edit' ? listing?.country_code || '' : knownCountry(unit?.building_country);
  const noCountry = mode === 'promote' && !country;
  const zone = mode === 'edit' ? listing?.building_timezone : unit?.building_timezone;
  const zoneName = zoneLabel(zone || '');
  const timeHint = zoneName ? `${w('buildingTime', desk)} (${zoneName})` : w('buildingTime', desk);
  const showZone = !!zone && zone !== deviceZone();
  const subtitle = mode === 'edit' && listing
    ? `${w('Unit', desk)} ${listing.unit_number} · ${listing.building_name}`
    : unit
    ? `${w('Unit', desk)} ${unit.unit_number} · ${unit.building_name}`
    : undefined;

  const update = (patch: Partial<Form>) => {
    setForm(current => ({ ...current, ...patch }));
    setErrors(current => {
      const next = { ...current };
      for (const k of Object.keys(patch)) delete next[k];

      return next;
    });
  };

  const localErrors = (): Record<string, string> => {
    const e: Record<string, string> = {};
    const name = form.display_name.trim();
    if (!name || name.length > 80) e.display_name = 'Give the listing a name of up to 80 characters.';
    if (!form.currency) e.currency = 'Choose a currency from the list.';
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(form.check_in_time)) e.check_in_time = 'Use a time like 15:00.';
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(form.check_out_time)) e.check_out_time = 'Use a time like 15:00.';

    return e;
  };

  const focusFirst = (errs: Record<string, string>, field?: string | null) => {
    const first = field && errs[field] ? field : FIELD_ORDER.find(f => errs[f]);
    if (!first) return;
    window.setTimeout(() => {
      const el = document.getElementById(fid(first));
      if (el) el.focus();
    }, 0);
  };

  const fail = (error: unknown, generic: string) => {
    setAttempt(a => a + 1);
    // The country has no field here: the server's word on it is the form's alert.
    if (error instanceof StaysApiError && error.status === 400 && error.fields.country_code) {
      setRefusal(error.fields.country_code);

      return;
    }
    if (error instanceof StaysApiError && error.status === 400 && Object.keys(error.fields).length) {
      setErrors(error.fields);
      focusFirst(error.fields, error.field);

      return;
    }
    if (error instanceof StaysApiError && error.code === 'STAYS_LISTING_CHANGED' && error.extra.listing) {
      toast.error('This listing changed since you opened it. It has been read again.');
      onSaved(error.extra.listing as StaysListing);
      onClose();

      return;
    }
    setRefusal(refusalText(error as StaysApiError, generic));
  };

  const submit = async (kind: 'live' | 'draft' | 'edit') => {
    if (busy.current) return;
    setRefusal(null);
    const errs = localErrors();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setAttempt(a => a + 1);
      focusFirst(errs);

      return;
    }
    busy.current = true;
    setSaving(kind);
    const name = form.display_name.trim();
    try {
      if (kind === 'edit' && listing) {
        const patch: StaysPatchInput = { version: listing.version };
        if (name !== listing.display_name) patch.display_name = name;
        if (form.currency !== listing.currency) patch.currency = form.currency;
        if (form.check_in_time !== listing.check_in_time) patch.check_in_time = form.check_in_time;
        if (form.check_out_time !== listing.check_out_time) patch.check_out_time = form.check_out_time;
        if (form.max_guests !== listing.max_guests) patch.max_guests = form.max_guests;
        if (form.host_user_id && form.host_user_id !== (listing.host?.id || '')) patch.host_user_id = form.host_user_id;
        if (Object.keys(patch).length === 1) {
          onClose();

          return;
        }
        const saved = await updateStaysListing(listing.id, patch);
        toast.success('Listing saved.');
        onSaved(saved);
        onClose();
      } else if (unit) {
        const saved = await promoteUnit({
          unit_id: unit.unit_id,
          display_name: name,
          currency: form.currency,
          check_in_time: form.check_in_time,
          check_out_time: form.check_out_time,
          max_guests: form.max_guests,
          as_draft: kind === 'draft',
        });
        toast.success(kind === 'draft' ? `${saved.display_name} is saved as a draft.` : `${saved.display_name} is on Stays.`);
        onSaved(saved);
        onClose();
      }
    } catch (error) {
      fail(error, kind === 'edit' ? 'Could not save the listing. Try again.' : 'Could not promote this unit. Try again.');
    } finally {
      busy.current = false;
      setSaving(null);
    }
  };

  const footer =
    mode === 'promote' ? (
      <>
        <AppButton variant="secondary" type="button" disabled={!!saving || noCountry} onClick={() => void submit('draft')}>
          <StatusLabel state={saving === 'draft' ? 'busy' : 'idle'} labels={{ idle: 'Save as draft', busy: 'Saving…' }} />
        </AppButton>
        <AppButton type="submit" form={formId} disabled={!!saving || noCountry}>
          <StatusLabel state={saving === 'live' ? 'busy' : 'idle'} labels={{ idle: 'Promote to Stays', busy: 'Promoting…' }} />
        </AppButton>
      </>
    ) : (
      <>
        <AppButton variant="secondary" type="button" disabled={!!saving} onClick={onClose}>
          Cancel
        </AppButton>
        <AppButton type="submit" form={formId} disabled={!!saving}>
          <StatusLabel state={saving ? 'busy' : 'idle'} labels={{ idle: 'Save changes', busy: 'Saving…' }} />
        </AppButton>
      </>
    );

  return (
    <FormModal
      open={open}
      title={mode === 'promote' ? 'Promote to Stays' : 'Edit listing basics'}
      subtitle={subtitle}
      onClose={onClose}
      isDirty={dirty}
      saving={!!saving}
      discardMessage={
        mode === 'promote'
          ? 'This unit has not been promoted yet. Close and lose what you have entered?'
          : 'Your changes to this listing have not been saved. Close anyway and lose them?'
      }
      footer={footer}
    >
      <Shake active={attempt > 0 && (Object.keys(errors).length > 0 || !!refusal)} attempt={attempt}>
        <form
          id={formId}
          ref={formRef}
          noValidate
          onSubmit={event => {
            event.preventDefault();
            if (noCountry) return;
            void submit(mode === 'edit' ? 'edit' : 'live');
          }}
        >
          {refusal ? <FormAlert role="alert">{refusal}</FormAlert> : null}

          <Field>
            <Label htmlFor={fid('display_name')}>Name shown to guests</Label>
            <Input
              id={fid('display_name')}
              value={form.display_name}
              maxLength={80}
              autoComplete="off"
              aria-invalid={errors.display_name ? true : undefined}
              aria-describedby={errors.display_name ? eid('display_name') : `${fid('display_name')}-hint`}
              hasError={!!errors.display_name}
              disabled={!!saving}
              data-autofocus
              onChange={event => update({ display_name: event.target.value })}
            />
            <FieldHint id={`${fid('display_name')}-hint`}>
              Guests see this name on their check-in page. Don&apos;t use a person&apos;s name.
            </FieldHint>
            <FieldError id={eid('display_name')} message={errors.display_name} />
          </Field>

          <FieldRow>
            <Field>
              <Label as="span" id={fid('country')}>
                Country
              </Label>
              <ReadValue aria-labelledby={fid('country')} data-testid="listing-country">
                {country ? countryName(country) : 'Not set'}
              </ReadValue>
              <FieldHint>
                {country ? `From the ${w('building', desk)}'s location.` : w('addLocationFirst', desk)}
              </FieldHint>
            </Field>
            <Field>
              <Label htmlFor={fid('currency')}>Currency</Label>
              <Select
                id={fid('currency')}
                value={form.currency}
                aria-invalid={errors.currency ? true : undefined}
                aria-describedby={errors.currency ? eid('currency') : undefined}
                disabled={!!saving}
                onChange={event => update({ currency: event.target.value })}
              >
                <option value="">Choose a currency</option>
                {STAYS_CURRENCIES.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Select>
              <FieldError id={eid('currency')} message={errors.currency} />
            </Field>
          </FieldRow>

          <FieldRow>
            <Field>
              <Label htmlFor={fid('check_in_time')}>Check-in from</Label>
              <Input
                id={fid('check_in_time')}
                type="time"
                value={form.check_in_time}
                aria-invalid={errors.check_in_time ? true : undefined}
                aria-describedby={errors.check_in_time ? eid('check_in_time') : `${fid('check_in_time')}-hint`}
                hasError={!!errors.check_in_time}
                disabled={!!saving}
                onChange={event => update({ check_in_time: event.target.value })}
              />
              <FieldHint id={`${fid('check_in_time')}-hint`}>{showZone ? timeHint : w('buildingTime', desk)}</FieldHint>
              <FieldError id={eid('check_in_time')} message={errors.check_in_time} />
            </Field>
            <Field>
              <Label htmlFor={fid('check_out_time')}>Check-out by</Label>
              <Input
                id={fid('check_out_time')}
                type="time"
                value={form.check_out_time}
                aria-invalid={errors.check_out_time ? true : undefined}
                aria-describedby={errors.check_out_time ? eid('check_out_time') : `${fid('check_out_time')}-hint`}
                hasError={!!errors.check_out_time}
                disabled={!!saving}
                onChange={event => update({ check_out_time: event.target.value })}
              />
              <FieldHint id={`${fid('check_out_time')}-hint`}>{showZone ? timeHint : w('buildingTime', desk)}</FieldHint>
              <FieldError id={eid('check_out_time')} message={errors.check_out_time} />
            </Field>
          </FieldRow>

          <Field>
            <Label id={fid('max_guests-label')}>Guests</Label>
            <Stepper
              value={form.max_guests}
              min={1}
              max={30}
              label="guests"
              disabled={!!saving}
              onChange={value => update({ max_guests: value })}
            />
            <FieldHint aria-live="polite">{`Guests, up to ${form.max_guests}`}</FieldHint>
            <FieldError id={eid('max_guests')} message={errors.max_guests} />
          </Field>

          {mode === 'edit' ? (
            <Field>
              <Label htmlFor={fid('host_user_id')}>Host</Label>
              <Select
                id={fid('host_user_id')}
                value={form.host_user_id}
                aria-invalid={errors.host_user_id ? true : undefined}
                aria-describedby={errors.host_user_id ? eid('host_user_id') : undefined}
                disabled={!!saving || hosts.length === 0}
                onChange={event => update({ host_user_id: event.target.value })}
              >
                {hosts.map(h => (
                  <option key={h.id} value={h.id}>
                    {h.name || 'Unnamed'}
                    {h.role === 'owner' ? ' (owner)' : ' (property manager)'}
                  </option>
                ))}
              </Select>
              <FieldError id={eid('host_user_id')} message={errors.host_user_id} />
            </Field>
          ) : null}
        </form>
      </Shake>
    </FormModal>
  );
};
